import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const ToastContext = createContext(null);

const DEFAULT_DURATION = {
  success: 2500,
  error: 6000,
};

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timeoutRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const dismissToast = useCallback(() => {
    clearTimer();
    setToast(null);
  }, [clearTimer]);

  const showToast = useCallback(
    (message, options = {}) => {
      const type = options.type || "success";
      const duration = options.duration ?? DEFAULT_DURATION[type] ?? DEFAULT_DURATION.success;
      clearTimer();
      setToast({ message, type });
      if (duration > 0) {
        timeoutRef.current = setTimeout(() => setToast(null), duration);
      }
    },
    [clearTimer]
  );

  useEffect(() => () => clearTimer(), [clearTimer]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {toast ? (
        <div className="fixed bottom-0 inset-x-0 pb-2 sm:pb-5 z-[100] pointer-events-none">
          <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 pointer-events-auto">
            <div
              className={`p-2 rounded-lg shadow-lg ${
                toast.type === "error" ? "bg-red-600" : "bg-green-600"
              }`}
              role={toast.type === "error" ? "alert" : "status"}
              aria-live={toast.type === "error" ? "assertive" : "polite"}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="ml-2 font-medium text-white text-sm">{toast.message}</p>
                <button
                  type="button"
                  className={`-mr-1 flex p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white ${
                    toast.type === "error" ? "hover:bg-red-500" : "hover:bg-green-500"
                  }`}
                  onClick={dismissToast}
                >
                  <span className="sr-only">Dismiss</span>
                  <XMarkIcon className="h-5 w-5 text-white" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) return { showToast: () => {}, dismissToast: () => {} };
  return ctx;
}
